import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SectionCard } from "./SectionCard";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/cn";

type Column<T> = {
  key: keyof T | string;
  header: string;
  align?: "start" | "end";
  accessor: (row: T) => React.ReactNode;
};

type TopListProps<T> = {
  size?: number;
  onSizeChange?: (size: number) => void;
  title: string;
  category: string;
  useData: (limit: number) => {
    data: T[] | undefined;
    isLoading: boolean;
  };
  columns: Column<T>[];
  chartKey: keyof T;
  chartLabelKey: keyof T;
  chartLabelFormatter?: (value: number) => string;
  rowKey: (row: T) => string;
};

const SIZE_OPTIONS = [5, 10, 25, 50];

export function TopList<T>({
  title,
  category,
  useData,
  columns,
  chartKey,
  chartLabelKey,
  chartLabelFormatter,
  rowKey,
  size: controlledSize,
  onSizeChange,
}: TopListProps<T>) {
  const { t } = useTranslation();
  const [localSize, setLocalSize] = React.useState<number>(5);
  const size = controlledSize ?? localSize;
  const setSize = onSizeChange ?? setLocalSize;
  const [view, setView] = React.useState<"table" | "chart">("table");

  const { data, isLoading } = useData(size);
  const rows = data ?? [];

  return (
    <SectionCard
      title={title}
      category={category}
      headerExtra={
        <div
          role="tablist"
          aria-label={t("usageAndCost.topList.sizeAriaLabel")}
          className="inline-flex max-w-full shrink-0 items-center gap-0.5 overflow-x-auto rounded-xl bg-secondary p-1"
        >
          {SIZE_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={size === s}
              onClick={() => setSize(s)}
              className={cn(
                "shrink-0 rounded-lg px-2 py-0.5 text-xs font-medium transition-colors",
                size === s
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {s}
            </button>
          ))}
        </div>
      }
      actions={
        <div
          role="tablist"
          aria-label={t("usageAndCost.topList.viewAriaLabel")}
          className="inline-flex shrink-0 items-center gap-0.5 rounded-xl bg-secondary p-1"
        >
          {(
            [
              ["table", "usageAndCost.topList.table"],
              ["chart", "usageAndCost.topList.chart"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={view === value}
              onClick={() => setView(value)}
              className={cn(
                "rounded-xl px-2.5 py-1 text-xs font-medium transition-colors",
                view === value
                  ? "rounded-lg bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t(label)}
            </button>
          ))}
        </div>
      }
      bodyClassName="p-0"
    >
      {view === "table" ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                {columns.map((col) => (
                  <th
                    key={String(col.key)}
                    className={cn(
                      "px-5 py-3 text-xs font-medium",
                      col.align === "end" ? "text-end" : "text-start",
                    )}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: size })
                  .slice(0, 5)
                  .map((_, i) => (
                    <tr
                      key={i}
                      className="border-b border-border last:border-b-0"
                    >
                      {columns.map((col) => (
                        <td key={String(col.key)} className="px-5 py-3">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-5 py-10 text-center text-sm text-muted-foreground"
                  >
                    {t("usageAndCost.topList.noData")}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={rowKey(row)}
                    className="border-b border-border last:border-b-0"
                  >
                    {columns.map((col) => (
                      <td
                        key={String(col.key)}
                        className={cn(
                          "px-5 py-3 text-foreground",
                          col.align === "end" ? "text-end" : "text-start",
                        )}
                      >
                        {col.accessor(row)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="h-[260px] w-full px-5 py-4" dir="ltr">
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : rows.length === 0 ? (
            <div className="flex h-full items-center justify-center text-center text-sm text-muted-foreground">
              {t("usageAndCost.topList.noData")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={rows as any[]}
                layout="vertical"
                margin={{ top: 5, right: 16, bottom: 0, left: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--border)"
                  opacity={0.4}
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={
                    chartLabelFormatter ?? ((v: number) => String(v))
                  }
                />
                <YAxis
                  type="category"
                  dataKey={chartLabelKey as string}
                  tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={140}
                />
                <Tooltip
                  cursor={{
                    fill: "var(--muted-foreground)",
                    fillOpacity: 0.08,
                  }}
                  contentStyle={{
                    backgroundColor: "var(--popover)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "var(--popover-foreground)",
                  }}
                  formatter={(value: number) =>
                    chartLabelFormatter ? chartLabelFormatter(value) : value
                  }
                />
                <Bar
                  dataKey={chartKey as string}
                  fill="#86c9a6"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </SectionCard>
  );
}
